// The `<doc>` wrapper of an authored-HTML document, and how a lost one is
// recognised and repaired (Martina Pachtová / Petra Litvínová, 2026-08-26).
//
// A strategy document is authored in the component language (dfy2-00 "Authored
// HTML"): it opens `<doc>`, closes `</doc>` and everything in between is the
// layout. TWO readers key on that OPENING tag - the reader (`isDocHtml`) and
// the AI editor (`launchpad_sales_editor.py`) - so a document that loses it is
// not merely untidy, it changes language:
//
//   1. an edit came back opening at `<lead>` (the model read the editor's
//      `===DOC===` delimiter AS the opening tag) and the editor's check looked
//      only for the CLOSING tag, so the wrapperless document was stored;
//   2. from then on the editor classified it as MARKDOWN and told the model to
//      preserve `##` and `:::` markers, so three further edits changed nothing;
//   3. the reader classified it as markdown too and printed `<p>` and `<lead>`
//      as visible text - "vidím tagy, v tom se nedá vůbec číst".
//
// One dropped tag therefore produced a document nobody could read and nobody
// could edit back into shape. Hence detection that TOLERATES the loss (so the
// next edit is at least made in the right language) and a repair applied where
// a document is stored, so a wrapperless one cannot be written again.
//
// This module has no imports: the browser (`src/doc/doc-parse.ts`) and the
// Worker (`worker/src/routes/automation-sales.ts`) both read it, and a second
// copy of the predicate is how the two would come to disagree about one
// document.

/** Block tags of the component language a document may legitimately open with. */
const DOC_BLOCK_TAGS = [
  "doc", "lead", "chapter", "p", "h", "split", "col", "grid", "card", "aside",
  "stats", "stat", "hero", "meters", "meter", "palette", "swatch", "steps", "step",
  "compare", "portrait", "scene", "quotes", "table", "row", "list", "check",
  "quote", "fold", "arc", "funnel", "scope", "wireframe", "evening", "calc",
  "timeline", "email", "segments", "punch", "figure",
];

const DOC_OPEN_RE = /^\s*<doc[\s>]/i;
const DOC_ANY_OPEN_RE = new RegExp(`^\\s*<(?:${DOC_BLOCK_TAGS.join("|")})[\\s>/]`, "i");

/** Does this source open with the wrapper the reader and the editor look for? */
export function hasDocWrapperOpen(source: string): boolean {
  return DOC_OPEN_RE.test(source);
}

/**
 * Is this the component language, wrapper or not?
 *
 * A document whose `<doc>` was dropped still CLOSES with `</doc>` and still
 * opens with one of its own block tags, so both halves are required: a
 * markdown document quoting `</doc>` in prose does not open with a block tag,
 * and an HTML page opens with `<html>`, which is not one either.
 */
export function looksLikeDocSource(source: string): boolean {
  if (hasDocWrapperOpen(source)) return true;
  return source.includes("</doc>") && DOC_ANY_OPEN_RE.test(source);
}

/**
 * Give a component document back a wrapper it lost.
 *
 * Idempotent, and it never touches a document that already carries both tags,
 * so it is safe to run over every stored document rather than only over the
 * one case that produced it.
 */
export function repairDocWrapper(source: string): string {
  let text = source.trim();
  if (!hasDocWrapperOpen(text)) text = `<doc>\n${text}`;
  if (!text.includes("</doc>")) text = `${text.replace(/\s+$/, "")}\n</doc>`;
  return text;
}
